import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { TransactionsFilter, TransactionStatus } from '../../../models/TransactionsModels';

import { resetBulkFilter, setBulkFilter } from '../../../stores/TransactionsStore';
import { RootState } from '../../../stores/Store';

import { DropdownItem } from '../../../components/Dropdown/DropdownModels';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Button from '../../../components/Button/Button';
import ButtonGroup from '../../../components/ButtonGroup/ButtonGroup';
import SearchInput from '../../../components/SearchInput/SearchInput';
import ClickableLinkWithIcon from '../../../components/Text/ClickableLinkWithIcon';
import FilterIcon from '../../../components/Icons/FilterIcon';
import { SuccessTickIconTable } from '../../../components/Icons/SuccessTickIcon';
import ResetIcon from '../../../components/Icons/ResetIcon';
import { FiltersBox, FilterTagsList } from '../../../components/FilterTag/FilterTagStyle';
import DownloadMenu from '../../../components/DownloadMenu/DownloadMenu';
import usePortalConfig from '../../../hooks/usePortalConfig/usePortalConfig';
import { FileFormat } from '../../../models/ImportModels';

interface TransactionsDetailsFiltersProps {
  onSearch: () => void;
  isLoading: boolean;
  exportData: (format: FileFormat) => void;
  totalItems: number;
  renderFilterTags: () => React.ReactNode[];
}

function TransactionsDetailsFilters({
  onSearch,
  isLoading = false,
  exportData,
  totalItems,
  renderFilterTags,
}: TransactionsDetailsFiltersProps) {
  const { configs } = usePortalConfig();
  const theme = useTheme();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { bulkFilter } = useSelector((state: RootState) => state.transactionsReducer);
  const [showFiltersArea, setShowFiltersArea] = useState(false);

  const statusOptions: DropdownItem[] = Object.keys(TransactionStatus)
    .sort()
    .map((st) => ({
      value: st,
      key: st,
      label: t(`pages.transactions.table.status.${st}`),
    }));

  /**
   * Search handler
   * @param f (filter)
   */
  const search = (f: string) => {
    onSearch();
    dispatch(setBulkFilter({ ...bulkFilter, filter: f }));
  };

  /**
   * Apply filters
   */
  const applyFilters = (vals: TransactionsFilter) => {
    dispatch(setBulkFilter(vals));
    setShowFiltersArea(false);
  };

  const formik = useFormik({
    initialValues: {
      status: bulkFilter.status || '',
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      applyFilters({ ...bulkFilter, ...formik.values });
    },
  });

  /**
   * Resets the form to the initial vals
   */
  const resetForm = () => {
    formik.resetForm();
    dispatch(resetBulkFilter());
  };

  return (
    <>
      <Grid container sx={{ paddingBottom: '1rem' }}>
        <Grid item xl={5} md={6} xs={12}>
          <SearchInput
            onSearch={(q) => {
              search(q);
            }}
            placeholder={t('pages.transactions.table.filters.searchPlaceholder')}
            isButtonDisabled={isLoading}
          />
        </Grid>
      </Grid>
      <FilterTagsList sx={{ padding: '1rem 0' }}>{renderFilterTags().map((n) => n)}</FilterTagsList>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body1" paddingBottom="1rem">
          {t('common.table.totalItems', { count: totalItems })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <ClickableLinkWithIcon
            wrapperSx={{ paddingLeft: '1rem', paddingRight: '1rem' }}
            sx={{ color: theme.palette.text.primary }}
            onClick={() => setShowFiltersArea(!showFiltersArea)}
            leftIcon={<FilterIcon />}
            disabled={isLoading}
          >
            {t('common.filter')}
          </ClickableLinkWithIcon>
          <DownloadMenu
            exportMaxNumberOfRows={configs.transactionsReport.exportMaxNumberOfRows}
            disabled={isLoading || totalItems === 0}
            downloadCSV={() => exportData('csv')}
            downloadExcel={() => exportData('xlsx')}
            itemLength={totalItems}
          />
        </Box>
      </Box>
      {showFiltersArea && (
        <FiltersBox>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ paddingBottom: '1.5rem' }}>
              <Dropdown
                id="status"
                name="status"
                label={t('pages.auditTrail.table.headers.status')}
                value={formik.values.status}
                onChange={(e) => formik.handleChange(e)}
                items={statusOptions}
                formStyle={{ maxWidth: '23.875rem' }}
                placeholder={
                  t('common.placeholders.dropdownPlaceholder') +
                  t('pages.auditTrail.table.headers.status').toLowerCase()
                }
              />
            </Box>
            <Box sx={{ marginTop: 'auto' }}>
              <ButtonGroup>
                <Button
                  label={
                    <>
                      <Box sx={{ marginRight: '0.5rem' }}>
                        <SuccessTickIconTable stroke={theme.palette.secondary.contrastText} />
                      </Box>
                      {t('common.applyFilters')}
                    </>
                  }
                  color="primaryButton"
                  disabled={!formik.isValid}
                  onClick={formik.submitForm}
                />
                <Button
                  label={
                    <>
                      <Box sx={{ marginRight: '0.5rem' }}>
                        <ResetIcon stroke={theme.palette.secondary.contrastText} />
                      </Box>
                      {t('common.resetFilters')}
                    </>
                  }
                  color="secondaryButton"
                  onClick={resetForm}
                />
              </ButtonGroup>
            </Box>
          </form>
        </FiltersBox>
      )}
    </>
  );
}

export default TransactionsDetailsFilters;
